import React from 'react';
import { Building2, MapPin, Stethoscope, Clock, Phone, Mail, ExternalLink, Repeat } from 'lucide-react';

// ClinicDetailsCard — read-only view of the clinic assigned to an order,
// shown after payment success (see PaymentReturn) so the customer knows
// where they'll be going. Populated by /orders/my with `assignedVendor`.
//
// Optional `onChangeClinic` callback (client 2026-09-09): if provided, a
// "Change clinic" button is rendered so the patient can re-open the
// picker and switch to a different clinic before arrival.
function ClinicDetailsCard({ clinic, onChangeClinic }) {
  if (!clinic) return null;
  const cp = clinic.clinicProfile || {};
  const addr = [cp.addressLine1, cp.addressLine2, cp.city, cp.state, cp.postcode]
    .filter(Boolean).join(', ');
  const mapsUrl = addr
    ? `https://maps.google.com/?q=${encodeURIComponent([cp.clinicName || clinic.name, addr].filter(Boolean).join(', '))}`
    : null;

  return (
    <div
      className="card"
      style={{
        marginTop: 20,
        padding: 20,
        textAlign: 'left',
        background: 'linear-gradient(180deg,#ecfdf5 0%,#d1fae5 100%)',
        border: '1px solid #6ee7b7',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Building2 size={18} color="#065f46" />
        <strong>Your assigned clinic</strong>
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
        {cp.clinicName || clinic.name || 'MyDr.AI panel clinic'}
      </div>

      <dl className="kvs" style={{ marginTop: 12, gridTemplateColumns: '1fr auto', gap: '6px 16px' }}>
        {addr && (
          <>
            <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <MapPin size={14} /> Address
            </dt>
            <dd style={{ textAlign: 'right', maxWidth: 260 }}>{addr}</dd>
          </>
        )}
        {cp.prescribingDoctor && (
          <>
            <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Stethoscope size={14} /> Doctor
            </dt>
            <dd style={{ textAlign: 'right' }}>{cp.prescribingDoctor}</dd>
          </>
        )}
        {cp.operatingHoursText && (
          <>
            <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={14} /> Hours
            </dt>
            <dd style={{ textAlign: 'right', maxWidth: 260 }}>{cp.operatingHoursText}</dd>
          </>
        )}
        {clinic.phone && (
          <>
            <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Phone size={14} /> Phone
            </dt>
            <dd style={{ textAlign: 'right' }}>
              <a href={`tel:${clinic.phone}`}>{clinic.phone}</a>
            </dd>
          </>
        )}
        {clinic.email && (
          <>
            <dt style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail size={14} /> Email
            </dt>
            <dd style={{ textAlign: 'right' }}>
              <a href={`mailto:${clinic.email}`}>{clinic.email}</a>
            </dd>
          </>
        )}
      </dl>

      {mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline btn-block"
          style={{ marginTop: 14, background: 'white' }}
        >
          <ExternalLink size={14} /> Open in Google Maps
        </a>
      )}

      {onChangeClinic && (
        <button
          type="button"
          onClick={onChangeClinic}
          className="btn btn-ghost btn-block"
          style={{ marginTop: 8 }}
        >
          <Repeat size={14} /> Change clinic
        </button>
      )}
    </div>
  );
}

export default ClinicDetailsCard;
